import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { StockRecapSummaryItem } from "@/app/api/types/stock";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";

interface StockCacheContextType {
  stockMap: Map<string, StockSummary>;
  stocks: StockRecapSummaryItem[];
}

const StockCacheContext = createContext<StockCacheContextType | null>(null);

export function StockCacheProvider({
  children,
  stockMap,
  stocks,
}: {
  children: ReactNode;
  stockMap: Map<string, StockSummary>;
  stocks: StockRecapSummaryItem[];
}) {
  // Memoise the wrapper so the Provider value identity only changes
  // when its members do — without this, every parent render allocates
  // a new `{ stockMap, stocks }` object and forces all consumers
  // (StockHistoryPopover, ProductCell, …) to re-render even though
  // the underlying map + array references are stable.
  const value = useMemo(() => ({ stockMap, stocks }), [stockMap, stocks]);
  return (
    <StockCacheContext.Provider value={value}>
      {children}
    </StockCacheContext.Provider>
  );
}

const EMPTY_STOCK_MAP = new Map<string, StockSummary>();
const EMPTY_STOCKS: StockRecapSummaryItem[] = [];

export function useStockCache(): StockCacheContextType {
  const context = useContext(StockCacheContext);
  if (!context) return { stockMap: EMPTY_STOCK_MAP, stocks: EMPTY_STOCKS };
  return context;
}
